import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';
import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SecurityIcon from '@mui/icons-material/Security';
import ComputerIcon from '@mui/icons-material/Computer';
import PageHeader from '../../components/common/PageHeader';
import api from '../../api/axiosInstance';

const getLicenseName = (license = {}) => license.licenseName || license.name || license.skuPartNumber || license.productName || 'Licence O365';
const toNumber = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getLevelOrganizationName = (device = {}) => device.matchedOrganizationName || device.matchedOrganization?.name || device.organizationName || '';
const getEsetOrganizationName = (license = {}) => license.organizationDetails?.name || license.organizationName || '';

function useClientOrganizationNames() {
  const { data: organizations = [] } = useSelector((state) => state.organizations.organizations);
  return useMemo(
    () => organizations.map((organization) => organization.name).filter(Boolean),
    [organizations],
  );
}

function filterByOrganization(items, organizationNames, getName) {
  const hasOrganizationName = items.some((item) => getName(item));
  if (!organizationNames.length || !hasOrganizationName) return items;
  return items.filter((item) => organizationNames.includes(getName(item)));
}

function SummaryCard({ icon, title, value, helper, color = 'primary.main' }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box sx={{ color, display: 'flex', mt: 0.5 }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">{title}</Typography>
          <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.1 }}>{value}</Typography>
          <Typography variant="caption" color="text.secondary">{helper}</Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function ClientO365Licenses() {
  const organizationNames = useClientOrganizationNames();
  const { data: o365Licenses = [], loading, error } = useSelector((state) => state.o365.licenses);
  const { data: esetLicenses = [], loading: esetLoading, error: esetError } = useSelector((state) => state.eset.licenses);
  const [levelDevices, setLevelDevices] = useState([]);
  const [levelLoading, setLevelLoading] = useState(true);
  const [levelError, setLevelError] = useState('');

  useEffect(() => {
    let mounted = true;
    setLevelLoading(true);
    setLevelError('');
    api.get('/api/level/devices?includeDeleted=false')
      .then(({ data }) => {
        if (mounted) setLevelDevices(data.devices || []);
      })
      .catch((err) => {
        if (mounted) setLevelError(err.response?.data?.message || err.response?.data?.error || 'Impossible de charger les appareils Level.io.');
      })
      .finally(() => {
        if (mounted) setLevelLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const clientName = organizationNames[0] || 'Client';
  const licenses = useMemo(() => filterByOrganization(
    o365Licenses,
    organizationNames,
    (license) => license.organizationName,
  ).filter((license) => toNumber(license.total) <= 1000), [o365Licenses, organizationNames]);

  const clientEsetLicenses = useMemo(() => filterByOrganization(
    esetLicenses,
    organizationNames,
    getEsetOrganizationName,
  ), [esetLicenses, organizationNames]);

  const clientLevelDevices = useMemo(() => filterByOrganization(
    levelDevices,
    organizationNames,
    getLevelOrganizationName,
  ), [levelDevices, organizationNames]);

  const summary = useMemo(() => {
    const o365Used = licenses.reduce((sum, license) => sum + toNumber(license.used), 0);
    const o365Total = licenses.reduce((sum, license) => sum + toNumber(license.total), 0);
    const esetUsed = clientEsetLicenses.reduce((sum, license) => sum + toNumber(license.used), 0);
    const esetTotal = clientEsetLicenses.reduce((sum, license) => sum + toNumber(license.quantity), 0);
    return {
      o365: { count: licenses.length, used: o365Used, total: o365Total },
      eset: { count: clientEsetLicenses.length, used: esetUsed, total: esetTotal },
      level: { count: clientLevelDevices.length },
    };
  }, [licenses, clientEsetLicenses, clientLevelDevices]);

  const columns = useMemo(() => [
    {
      accessorFn: getLicenseName,
      id: 'licenseName',
      header: 'Licence',
      size: 260,
      filterVariant: 'autocomplete',
      Cell: ({ renderedCellValue }) => <Typography sx={{ fontWeight: 700 }}>{renderedCellValue}</Typography>,
    },
    {
      accessorFn: (row) => toNumber(row.used),
      id: 'used',
      header: 'Utilisées',
      size: 120,
    },
    {
      accessorFn: (row) => toNumber(row.total),
      id: 'total',
      header: 'Total',
      size: 120,
    },
    {
      accessorFn: (row) => Math.max(toNumber(row.total) - toNumber(row.used), 0),
      id: 'available',
      header: 'Disponibles',
      size: 140,
      Cell: ({ cell }) => {
        const value = toNumber(cell.getValue());
        return <Chip size="small" color={value > 0 ? 'success' : 'warning'} label={value} />;
      },
    },
    {
      accessorFn: (row) => {
        const total = toNumber(row.total);
        if (!total) return 0;
        return Math.min((toNumber(row.used) / total) * 100, 100);
      },
      id: 'usage',
      header: 'Occupation',
      size: 220,
      Cell: ({ row }) => {
        const used = toNumber(row.original.used);
        const total = toNumber(row.original.total);
        const percent = total ? Math.min((used / total) * 100, 100) : 0;
        return (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 180 }}>
            <LinearProgress
              variant="determinate"
              value={percent}
              color={percent >= 100 ? 'error' : percent >= 85 ? 'warning' : 'primary'}
              sx={{ height: 8, borderRadius: 5, flex: 1 }}
            />
            <Typography variant="caption" color="text.secondary">{Math.round(percent)}%</Typography>
          </Stack>
        );
      },
    },
    {
      accessorFn: (row) => formatDate(row.nextCycleDateTime),
      id: 'nextCycleDateTime',
      header: 'Renouvellement',
      size: 180,
    },
  ], []);

  const table = useMaterialReactTable({
    columns,
    data: loading ? [] : licenses,
    localization: MRT_Localization_FR,
    enableColumnFilterModes: true,
    enableColumnFilters: true,
    enableColumnOrdering: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    enableGlobalFilter: true,
    initialState: {
      density: 'compact',
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageSize: 50 },
    },
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
      placeholder: 'Rechercher',
    },
    muiPaginationProps: {
      rowsPerPageOptions: [25, 50, 100],
      shape: 'rounded',
      variant: 'outlined',
    },
    paginationDisplayMode: 'pages',
    positionGlobalFilter: 'right',
    state: { isLoading: loading },
    renderDetailPanel: ({ row }) => {
      const users = Array.isArray(row.original.matchedUsers) ? row.original.matchedUsers : [];
      if (!users.length) return null;
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Utilisateurs associés</Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {users.map((user, index) => (
              <Chip
                key={`${user.userPrincipalName || user.displayName || index}`}
                label={user.displayName || user.userPrincipalName || 'Utilisateur'}
                size="small"
                variant="outlined"
              />
            ))}
          </Stack>
        </Box>
      );
    },
  });

  return (
    <Box>
      <PageHeader section={clientName} title="Vos licences" sx={{ my: 1, mb: 3 }} />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2, mb: 3 }}>
        <SummaryCard
          icon={<MailOutlineIcon />}
          title="Licences O365"
          value={loading ? '...' : summary.o365.count}
          helper={`${summary.o365.used} utilisées / ${summary.o365.total} prévues`}
          color="info.main"
        />
        <SummaryCard
          icon={<SecurityIcon />}
          title="Licences ESET"
          value={esetLoading ? '...' : summary.eset.count}
          helper={`${summary.eset.used} utilisées / ${summary.eset.total} prévues`}
          color="success.main"
        />
        <SummaryCard
          icon={<ComputerIcon />}
          title="Level.io"
          value={levelLoading ? '...' : summary.level.count}
          helper="appareil(s) supervisé(s)"
          color="primary.main"
        />
      </Box>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>Erreur de chargement des licences O365.</Alert> : null}
      {esetError ? <Alert severity="error" sx={{ mb: 2 }}>Erreur de chargement des licences ESET.</Alert> : null}
      {levelError ? <Alert severity="error" sx={{ mb: 2 }}>{levelError}</Alert> : null}

      <Typography variant="h6" sx={{ fontWeight: 900, mb: 1.5 }}>Détail des licences O365</Typography>
      {loading ? (
        <Skeleton variant="rectangular" width="100%" height={520} />
      ) : licenses.length === 0 ? (
        <Box sx={{ textAlign: 'center', p: 4, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="h6" color="text.secondary">Aucune licence O365 trouvée</Typography>
        </Box>
      ) : (
        <MaterialReactTable table={table} />
      )}
    </Box>
  );
}
